/**
 * Multi-page player survey modal on /profile.
 * Drafts persist in localStorage; submit/retake via playerSurvey.ts helpers.
 * See docs/flows/player-survey.md and docs/player-survey.md.
 */
import { useContext, useEffect, useState } from 'react';
import {
  Button,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FirebaseContext } from '@/session/FirebaseContext';
import { SurveyProgressBar, SurveyQuestionField } from '@/components/survey/SurveyQuestionField';
import {
  PLAYER_SURVEY_FRAMING,
  PLAYER_SURVEY_PAGE_COUNT,
  PLAYER_SURVEY_PAGES,
  type PlayerSurveyAnswerKey,
} from '@/constants/playerSurvey';
import { MODAL_FOOTER_PB, MODAL_HEADER_PT } from '@/constants/mobileLayout';
import { useMobileFormModal } from '@/hooks/useMobileFormModal';
import {
  loadPlayerSurveyDraft,
  savePlayerSurveyDraft,
  submitPlayerSurvey,
  type PlayerSurveyAnswers,
} from '@/models/playerSurvey';

export function PlayerSurveyModal(props: {
  isOpen: boolean;
  onClose: () => void;
  uid: string;
  /** Admin retake: overwrite existing response instead of create-once. */
  retake?: boolean;
  /** Prefill when retaking (Firestore answers); draft still wins if present. */
  savedAnswers?: PlayerSurveyAnswers;
}) {
  const { isOpen, onClose, uid, retake = false, savedAnswers } = props;
  const ctx = useContext(FirebaseContext);
  const toast = useToast();
  const { modalSize, isCentered, scrollBehavior, contentProps, bodyProps, footerPb } = useMobileFormModal();

  const [pageIndex, setPageIndex] = useState(1);
  const [answers, setAnswers] = useState<PlayerSurveyAnswers>({});
  const [submitting, setSubmitting] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setHydrated(false);
      return;
    }
    const draft = loadPlayerSurveyDraft(uid);
    if (draft) {
      setAnswers(draft.answers);
      setPageIndex(draft.pageIndex);
    } else if (retake && savedAnswers) {
      setAnswers(savedAnswers);
      setPageIndex(1);
    } else {
      setAnswers({});
      setPageIndex(1);
    }
    setHydrated(true);
  }, [isOpen, uid, retake, savedAnswers]);

  useEffect(() => {
    if (!isOpen || !hydrated) return;
    savePlayerSurveyDraft(uid, { answers, pageIndex });
  }, [answers, pageIndex, uid, isOpen, hydrated]);

  const page = PLAYER_SURVEY_PAGES[pageIndex - 1];
  const isLastPage = pageIndex >= PLAYER_SURVEY_PAGE_COUNT;

  const handleAnswerChange = (
    key: PlayerSurveyAnswerKey,
    value: string | string[] | number | undefined,
  ) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (value === undefined) {
        delete next[key];
      } else {
        next[key] = value;
      }
      if (key === 'q8' && value !== 'Other') {
        delete next.q8a;
      }
      return next;
    });
  };

  const handleClose = () => {
    savePlayerSurveyDraft(uid, { answers, pageIndex });
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await submitPlayerSurvey(ctx.db, uid, answers, { retake });
      toast({
        title: retake ? 'Survey updated' : 'Thanks for your feedback',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not submit survey';
      toast({
        title: 'Submit failed',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered={isCentered}
      size={modalSize ?? { base: 'full', md: 'xl' }}
      scrollBehavior={scrollBehavior}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent {...contentProps} maxW={{ md: '560px' }}>
        <ModalHeader pt={MODAL_HEADER_PT} pb={3} flexShrink={0} textAlign='left' pr={12}>
          <Heading fontSize={{ base: 'xl', md: '2xl' }} color='chakra-heading-text' textAlign='left' mb={3}>
            Quick survey
          </Heading>
          <SurveyProgressBar pageIndex={pageIndex} pageCount={PLAYER_SURVEY_PAGE_COUNT} />
        </ModalHeader>
        <ModalCloseButton mt={2} mr={2} />
        <ModalBody {...bodyProps} px={{ base: 5, md: 8 }} pb={4}>
          <VStack spacing={4} align='stretch' flexShrink={0} w='full'>
            {pageIndex === 1 ? (
              <Text textAlign='left' color='chakra-body-text' fontSize='sm' flexShrink={0}>
                {PLAYER_SURVEY_FRAMING}
              </Text>
            ) : null}
            {page.questions.map((question) => (
              <SurveyQuestionField
                key={question.id}
                question={question}
                answers={answers}
                onChange={handleAnswerChange}
              />
            ))}
          </VStack>
        </ModalBody>
        <ModalFooter pb={footerPb ?? MODAL_FOOTER_PB} flexShrink={0} px={{ base: 5, md: 8 }}>
          <HStack w='full' spacing={3}>
            <Button
              variant='ghost'
              onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
              isDisabled={pageIndex <= 1 || submitting}
            >
              Back
            </Button>
            <Spacer />
            {isLastPage ? (
              <Button colorScheme='blue' onClick={() => void handleSubmit()} isLoading={submitting}>
                Submit
              </Button>
            ) : (
              <Button
                colorScheme='blue'
                onClick={() => setPageIndex((p) => Math.min(PLAYER_SURVEY_PAGE_COUNT, p + 1))}
                isDisabled={submitting}
              >
                Next
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
